import React from 'react';
import {FormField, FormSection, FormActions} from '../../../../Presentation/components/common';
import {useFormState, CommonValidationRules, createFieldProps} from '../../../../Presentation/utils/formUtils';
import Card from '../../../../Presentation/components/ui/Card';
import {AppText, AppTitle, AppCardTitle, AppSmallText} from '../../../../Presentation/components/ui/Typography';
import {
  UserCog
} from 'lucide-react';
import StandardizedErrorHandler from '../../../../Services/common/StandardizedErrorHandler';

interface CreateRoleFormProps {
  onSubmit: (roleData: {
    name: string;
    displayName: string;
    description: string;
  }) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const CreateRoleForm: React.FC<CreateRoleFormProps> = ({
  onSubmit,
  onCancel,
  loading = false
}) => {
  // Centralized form state for creating new role
  const [formState, formActions] = useFormState(
    {
      name: '',
      displayName: '',
      description: ''
    },
    {
      name: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'اسم الدور مطلوب';
          }
          if (!/^[a-z_]+$/.test(value)) {
            return 'اسم الدور يجب أن يحتوي على أحرف صغيرة وشرطات سفلية فقط';
          }
          if (value === 'admin' || value === 'super_admin') {
            return 'هذا الاسم محجوز للنظام';
          }
          return null;
        }
      },
      displayName: CommonValidationRules.required('اسم العرض'),
      description: CommonValidationRules.required('وصف الدور')
    }
  );

  const handleSubmit = async (data: any) => {
    try {
      await onSubmit(data);
    } catch (error) {
      // Handle error using StandardizedErrorHandler to show server message
      StandardizedErrorHandler.getInstance().handleError(error, 'CreateRoleForm.handleSubmit');
    }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); formActions.submit(handleSubmit); }} className="space-y-6">
      {/* Basic Information */}
      <Card className="p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-2 bg-brand-100 rounded-full">
            <UserCog className="w-5 h-5 text-brand-600" />
          </div>
          <AppCardTitle>
            إنشاء دور جديد
          </AppCardTitle>
        </div>

        <FormSection title="معلومات الدور">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <FormField
                type="text"
                label="اسم الدور (تقني) *"
                placeholder="مثال: custom_manager"
                {...createFieldProps('name', formState, formActions)}
              />
              <AppSmallText className="text-gray-500 mt-1">
                يجب أن يحتوي على أحرف صغيرة وشرطات سفلية فقط
              </AppSmallText>
            </div>

            <FormField
              type="text"
              label="اسم العرض *"
              placeholder="مثال: مدير مخصص"
              {...createFieldProps('displayName', formState, formActions)}
            />
          </div>

          <FormField
            type="textarea"
            label="وصف الدور *"
            placeholder="وصف مختصر لدور المستخدم وصلاحياته..."
            rows={3}
            {...createFieldProps('description', formState, formActions)}
          />
        </FormSection>
      </Card>

      <FormActions
        onSubmit={() => formActions.submit(handleSubmit)}
        onCancel={onCancel}
        submitLabel="إنشاء الدور"
        cancelLabel="إلغاء"
        isLoading={loading || formState.isSubmitting}
        disabled={!formState.isValid}
        showReset={false}
      />
    </form>
  );
};
